import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaCheckCircle, FaBox, FaArrowLeft } from 'react-icons/fa';
import { doc, getDoc } from 'firebase/firestore';
import { db, collections } from '../../firebase.marketplace';
import { useAuth } from '../../contexts/AuthContext';

const Container = styled.div`
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const ConfirmationCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 15px;
  padding: 2rem;
  margin-bottom: 2rem;
  text-align: center;
`;

const SuccessIcon = styled.div`
  font-size: 4rem;
  color: #4CAF50;
  margin-bottom: 1.5rem;
`;

const Title = styled.h1`
  color: white;
  font-size: 2rem;
  margin-bottom: 1rem;
`;

const Message = styled.p`
  color: #aaa;
  font-size: 1.2rem;
  margin-bottom: 2rem;
`;

const OrderDetails = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 15px;
  padding: 2rem;
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h2`
  color: white;
  font-size: 1.5rem;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  svg {
    color: #4a90e2;
  }
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const DetailSection = styled.div`
  margin-bottom: 1.5rem;
`;

const DetailLabel = styled.div`
  color: #888;
  font-size: 0.9rem;
  margin-bottom: 0.5rem;
`;

const DetailValue = styled.div`
  color: white;
  font-size: 1.1rem;
`;

const ItemsList = styled.div`
  margin-top: 1.5rem;
`;

const Item = styled.div`
  display: grid;
  grid-template-columns: 3fr 1fr 1fr;
  gap: 1rem;
  padding: 1rem 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);

  &:last-child {
    border-bottom: none;
  }

  @media (max-width: 600px) {
    grid-template-columns: 2fr 1fr;
    grid-template-rows: auto auto;
    gap: 0.5rem;
  }
`;

const ItemHeader = styled(Item)`
  color: #888;
  font-size: 0.9rem;
  padding-bottom: 0.5rem;
`;

const ItemName = styled.div`
  color: white;

  @media (max-width: 600px) {
    grid-column: 1 / span 2;
  }
`;

const ItemQuantity = styled.div`
  color: #aaa;
  text-align: center;
`;

const ItemPrice = styled.div`
  color: #4a90e2;
  text-align: right;
  font-weight: 600;

  @media (max-width: 600px) {
    grid-column: 2;
    grid-row: 2;
  }
`;

const TotalSection = styled.div`
  display: flex;
  justify-content: space-between;
  padding-top: 1.5rem;
  margin-top: 1.5rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`;

const TotalLabel = styled.div`
  color: white;
  font-size: 1.2rem;
  font-weight: 600;
`;

const TotalValue = styled.div`
  color: #4a90e2;
  font-size: 1.2rem;
  font-weight: 600;
`;

const ContinueShoppingButton = styled(motion.button)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 1rem 2rem;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  cursor: pointer;
  margin: 0 auto;
  
  &:hover {
    background: rgba(255, 255, 255, 0.15);
  }
`;

const LoadingMessage = styled.div`
  color: #aaa;
  text-align: center;
  padding: 2rem;
  font-size: 1.2rem;
`;

const ErrorMessage = styled.div`
  color: #ff4d4d;
  text-align: center;
  padding: 2rem;
  font-size: 1.2rem;
`;

const OrderConfirmation = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        setLoading(true);
        const orderDoc = await getDoc(doc(db, 'orders', orderId));
        
        if (!orderDoc.exists()) {
          setError('Order not found');
          return;
        }
        
        const orderData = orderDoc.data();
        
        // Check if the order belongs to the current user
        if (orderData.userId !== 'guest' && 
            orderData.userId !== currentUser?.uid) {
          setError('You do not have permission to view this order');
          return;
        }
        
        setOrder({
          id: orderDoc.id,
          ...orderData
        });
      } catch (err) {
        console.error('Error fetching order:', err);
        setError('Failed to load order details');
      } finally {
        setLoading(false);
      }
    };
    
    if (orderId) {
      fetchOrder();
    }
  }, [orderId, currentUser]);

  if (loading) {
    return (
      <Container>
        <LoadingMessage>Loading order details...</LoadingMessage>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <ErrorMessage>{error}</ErrorMessage>
        <ContinueShoppingButton
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => navigate('/marketplace')}
        >
          <FaArrowLeft /> Continue Shopping
        </ContinueShoppingButton>
      </Container>
    );
  }

  if (!order) {
    return (
      <Container>
        <ErrorMessage>Order not found</ErrorMessage>
        <ContinueShoppingButton
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => navigate('/marketplace')}
        >
          <FaArrowLeft /> Continue Shopping
        </ContinueShoppingButton>
      </Container>
    );
  }

  // Format date
  const orderDate = order.createdAt ? new Date(order.createdAt.seconds * 1000) : new Date();
  const formattedDate = orderDate.toLocaleDateString('en-UG', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  return (
    <Container>
      <ConfirmationCard>
        <SuccessIcon>
          <FaCheckCircle />
        </SuccessIcon>
        <Title>Order Confirmed!</Title>
        <Message>
          Thank you for your purchase. Your order has been received and is being processed.
        </Message>
      </ConfirmationCard>

      <OrderDetails>
        <SectionTitle>
          <FaBox /> Order Details
        </SectionTitle>
        <DetailGrid>
          <DetailSection>
            <DetailLabel>Order Number</DetailLabel>
            <DetailValue>{order.id}</DetailValue>
          </DetailSection>
          <DetailSection>
            <DetailLabel>Date</DetailLabel>
            <DetailValue>{formattedDate}</DetailValue>
          </DetailSection>
          <DetailSection>
            <DetailLabel>Status</DetailLabel>
            <DetailValue style={{ color: order.status === 'processing' ? '#4CAF50' : '#FFC107' }}>
              {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
            </DetailValue>
          </DetailSection>
          <DetailSection>
            <DetailLabel>Payment Method</DetailLabel>
            <DetailValue>
              {order.paymentMethod === 'card' ? 'Credit Card' : 'Mobile Money'}
            </DetailValue>
          </DetailSection>
        </DetailGrid>

        <SectionTitle>Items</SectionTitle>
        <ItemsList>
          <ItemHeader>
            <div>Product</div>
            <div style={{ textAlign: 'center' }}>Quantity</div>
            <div style={{ textAlign: 'right' }}>Price</div>
          </ItemHeader>
          {order.items.map((item, index) => (
            <Item key={index}>
              <ItemName>{item.title}</ItemName>
              <ItemQuantity>{item.quantity}</ItemQuantity>
              <ItemPrice>
                {(item.price * item.quantity).toLocaleString('en-UG')} UGX
              </ItemPrice>
            </Item>
          ))}
        </ItemsList>

        <TotalSection>
          <TotalLabel>Total</TotalLabel>
          <TotalValue>{order.total.toLocaleString('en-UG')} UGX</TotalValue>
        </TotalSection>
      </OrderDetails>

      <ContinueShoppingButton
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        onClick={() => navigate('/marketplace')}
      >
        <FaArrowLeft /> Continue Shopping
      </ContinueShoppingButton>
    </Container>
  );
};

export default OrderConfirmation;
